"use client"

import { useMemo, useState } from "react"
import { Card } from "~/components/legacy/common/card"
import { Input } from "~/components/legacy/common/input"
import { Link } from "~/components/legacy/common/link"
import { ArrowRight, SearchIcon } from "~/components/ui/icons"
import { Favicon } from "~/components/web/ui/favicon"
import { sortAlternativesByDemand } from "~/config/alternatives-demand"
import { getAlternativesPath } from "~/lib/seo-alternatives"
import type { WebAlternativesPage } from "~/server/web/alternatives/queries"

const workflows = [
  { value: "all", label: "All comparisons" },
  { value: "browser-automation", label: "Browser-based scraping" },
  { value: "web-scraping", label: "Scraping APIs" },
  { value: "serp-extraction", label: "SERP APIs" },
] as const

export function AlternativeDirectory({ pages }: { pages: WebAlternativesPage[] }) {
  const [query, setQuery] = useState("")
  const [workflow, setWorkflow] = useState("all")
  const [sort, setSort] = useState("name")
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase()
    const filtered = pages.filter(
      page =>
        (workflow === "all" || page.sourcePrimaryTask === workflow) &&
        (!term || `${page.sourceName} ${page.description}`.toLowerCase().includes(term)),
    )
    if (sort === "name")
      return [...filtered].sort((a, b) => a.sourceName.localeCompare(b.sourceName))
    if (sort === "options")
      return [...filtered].sort(
        (a, b) =>
          b.alternatives.length - a.alternatives.length || a.sourceName.localeCompare(b.sourceName),
      )
    return sortAlternativesByDemand(filtered)
  }, [pages, query, workflow, sort])

  return (
    <div
      className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_15rem]"
      id="alternatives-directory"
    >
      <section className="min-w-0 space-y-5" aria-label="Tool comparisons">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative min-w-0 grow">
            <label htmlFor="alternatives-search" className="sr-only">
              Search alternatives
            </label>
            <SearchIcon
              className="pointer-events-none absolute left-3 top-3.5 z-10 size-4 text-secondary-foreground"
              aria-hidden="true"
            />
            <Input
              id="alternatives-search"
              size="lg"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search alternatives..."
              className="min-h-11 pl-10"
            />
          </div>
          <label className="sr-only" htmlFor="alternatives-sort">
            Sort comparisons
          </label>
          <select
            id="alternatives-sort"
            value={sort}
            onChange={event => setSort(event.target.value)}
            className="min-h-11 rounded-md border bg-background px-3 text-sm focus-visible:outline-primary sm:w-44"
          >
            <option value="popular">Most searched</option>
            <option value="options">Most alternatives</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>
        <p className="text-sm text-secondary-foreground" role="status">
          {visible.length} {visible.length === 1 ? "comparison" : "comparisons"}
        </p>
        <div className="space-y-4" id="alternative-results">
          {visible.map(page => (
            <Card key={page.sourceSlug} className="items-stretch gap-3 rounded-lg p-5" asChild>
              <Link href={getAlternativesPath(page.sourceSlug)}>
                <div
                  className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3"
                  data-alternative-header
                >
                  <Favicon src={page.sourceFaviconUrl} title={page.sourceName} className="size-9" />
                  <h2 className="min-w-0 break-words text-lg font-semibold">
                    {page.sourceName} alternatives
                  </h2>
                  <span
                    className="rounded-full border bg-background px-2.5 py-1 text-xs tabular-nums whitespace-nowrap text-secondary-foreground"
                    data-option-count
                  >
                    {page.alternatives.length} options
                  </span>
                </div>
                <p className="text-sm/relaxed text-secondary-foreground">{page.description}</p>
                <div className="mt-1 flex w-full flex-wrap items-center justify-between gap-x-5 gap-y-2 border-t pt-3 text-sm">
                  <span className="text-secondary-foreground">
                    {page.alternatives
                      .slice(0, 3)
                      .map(item => item.name)
                      .join(", ")}
                    {page.alternatives.length > 3 ? " + more" : ""}
                  </span>
                  <span className="inline-flex items-center gap-2 font-medium text-primary dark:text-indigo-300">
                    Compare <ArrowRight className="size-4" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </Card>
          ))}
        </div>
        {!visible.length && (
          <div className="rounded-lg border p-6">
            <h2 className="font-semibold">No comparisons found</h2>
            <p className="mt-2 text-sm text-secondary-foreground">
              Try another tool name or browse all workflows.
            </p>
            <button
              type="button"
              onClick={() => {
                setQuery("")
                setWorkflow("all")
              }}
              className="mt-3 min-h-11 font-medium text-primary dark:text-indigo-300 underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </section>
      <aside
        className="order-first min-w-0 space-y-6 lg:order-last lg:sticky lg:top-24"
        aria-label="Browse comparisons"
      >
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold">Browse by workflow</h2>
          <div className="flex flex-wrap gap-1 lg:flex-col">
            {workflows.map(item => (
              <button
                key={item.value}
                type="button"
                aria-pressed={workflow === item.value}
                onClick={() => setWorkflow(item.value)}
                className={`flex min-h-11 items-center justify-between gap-3 rounded-md px-3 text-left text-sm ${workflow === item.value ? "bg-accent font-medium text-foreground" : "text-secondary-foreground hover:bg-accent hover:text-foreground"}`}
              >
                <span>{item.label}</span>
                <span className="tabular-nums text-xs">
                  {item.value === "all"
                    ? pages.length
                    : pages.filter(page => page.sourcePrimaryTask === item.value).length}
                </span>
              </button>
            ))}
          </div>
        </section>
        <nav
          className="hidden space-y-3 rounded-lg border bg-card p-4 lg:block"
          aria-label="Popular comparisons"
        >
          <h2 className="text-sm font-semibold">Popular alternatives</h2>
          {sortAlternativesByDemand(pages)
            .slice(0, 6)
            .map(page => (
              <Link
                key={page.sourceSlug}
                href={getAlternativesPath(page.sourceSlug)}
                className="flex min-h-10 w-full items-center gap-3 text-sm text-secondary-foreground hover:text-primary dark:hover:text-indigo-300"
              >
                <span className="min-w-0 break-words">{page.sourceName}</span>
                <span className="ml-auto whitespace-nowrap text-xs tabular-nums">
                  {page.alternatives.length} options
                </span>
              </Link>
            ))}
        </nav>
        <section className="hidden space-y-2 rounded-lg border p-4 text-sm lg:block">
          <h2 className="font-semibold">Need a different starting point?</h2>
          <p className="text-secondary-foreground">
            Browse tools by language, deployment and access model.
          </p>
          <Link
            href="/tools"
            className="inline-flex min-h-11 items-center gap-2 font-medium text-primary dark:text-indigo-300"
          >
            Find a tool <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </section>
      </aside>
    </div>
  )
}
